import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import { SaveIcon } from "../../../utils/icons/icons";
import { useTranslation } from "react-i18next";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import ExcludedDate, { getRepeatAnnuallyString } from "../../../interfaces/robot/excluded-dates/excluded-date";
import { SelectChangeEvent } from "@mui/material/Select";
import { validateEndDate, validateReason, validateStartDate } from "../../../validation/excluded-dates-validation";
import DateInput from "../../Inputs/DateInput";
import TextInput from "../../Inputs/TextInput";
import { DateTime } from "luxon";
import { parseDateString, toDateString } from "../../../utils/date";
import RoomData from "../../../interfaces/robot/rooms/room-data";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import ValidationResult from "../../../interfaces/api/validation-result";
import ReadOnlyProtectedButton from "../../Inputs/ReadOnlyProtectedButton";
import { selectExcludedDates } from "../../../store/robot-configurations/robot-configurations-selectors";
import DialogWrapperWithClose from "../../General/DialogWrapperWithClose";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import useValidatedInputEffect from "../../../hooks/useValidatedInputEffect";
import useServerValidationResponseEffect from "../../../hooks/useServerValidationResponseEffect";
import RoomSelect from "../../Room/RoomSelectors/RoomSelect";

const ExcludedDateDetails: React.FC<{
  editedDate: ExcludedDate | null;
  onDialogClose: () => void;
  availableRooms: RoomData[];
}> = ({ editedDate, onDialogClose, availableRooms }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { patchExcludedDateById, postExcludedDate, patchExcludedDatesRestSet } = useSharedActions();

  const excludedDatesData = useSharedSelector((state) => selectExcludedDates(state));

  const [startDate, setStartDate] = useState<{ value: DateTime; validation: ValidationResult }>({
    value: editedDate ? parseDateString(editedDate.startDate) : DateTime.now(),
    validation: {
      isValid: editedDate
        ? validateStartDate(parseDateString(editedDate.startDate)).isValid
        : validateStartDate(DateTime.now()).isValid,
      error: null,
    },
  });

  const [endDate, setEndDate] = useState<{ value: DateTime; validation: ValidationResult }>({
    value: editedDate ? parseDateString(editedDate.endDate) : DateTime.now(),
    validation: {
      isValid: editedDate
        ? validateEndDate(parseDateString(editedDate.endDate), parseDateString(editedDate.startDate)).isValid
        : validateEndDate(DateTime.now(), DateTime.now()).isValid,
      error: null,
    },
  });

  const [roomIds, setRoomIds] = useState<string[]>(editedDate?.roomIds ?? []);

  const [repeatAnnually, setRepeatAnnually] = useState(editedDate?.repeatAnnually ?? false);

  useEffect(() => {
    if (
      excludedDatesData.rest.postStatus === HttpFetchStatus.Success ||
      excludedDatesData.rest.patchStatus === HttpFetchStatus.Success
    ) {
      onDialogClose();
    }
  }, [excludedDatesData.rest.postStatus, excludedDatesData.rest.patchStatus, onDialogClose]);

  const { resetToggle, removeServerError, getServerValidationResultOrNull } = useServerValidationResponseEffect({
    patchError: excludedDatesData.rest.patchError,
    resetPatchHandler: () =>
      dispatch(patchExcludedDatesRestSet({ patchStatus: HttpFetchStatus.Idle, patchError: null })),
    postError: excludedDatesData.rest.postError,
    resetPostHandler: () => dispatch(patchExcludedDatesRestSet({ postStatus: HttpFetchStatus.Idle, postError: null })),
  });

  const {
    value: reason,
    validationResult: reasonValidationResult,
    valueChangeHandler: reasonChangeHandler,
  } = useValidatedInputEffect(
    editedDate?.reason ?? "",
    (newReason) => validateReason(newReason),
    (_) => () => {},
    getServerValidationResultOrNull("reason"),
    removeServerError,
    resetToggle
  );

  const startDateChangeHandler = (newStartDate: DateTime | null) => {
    if (newStartDate) {
      setStartDate({ value: newStartDate, validation: validateStartDate(newStartDate) });

      if (endDate.value < newStartDate) {
        setEndDate({ value: newStartDate, validation: validateEndDate(newStartDate, newStartDate) });
      }
    }
  };

  const endDateChangeHandler = (newEndDate: DateTime | null) => {
    if (newEndDate) {
      setEndDate({ value: newEndDate, validation: validateEndDate(newEndDate, startDate.value) });
    }
  };

  const roomIdsChangeHandler = (event: SelectChangeEvent<string[]>) => {
    if (typeof event.target.value !== "string") {
      setRoomIds(event.target.value);
    }
  };

  const repeatAnnuallyChangeHandler = () => {
    setRepeatAnnually((prevState) => !prevState);
  };

  const applyClickHandler = (_event: React.MouseEvent) => {
    const formContent = {
      startDate: toDateString(startDate.value),
      endDate: toDateString(endDate.value),
      roomIds,
      reason,
      repeatAnnually,
    };
    if (editedDate?.id != null) {
      dispatch(patchExcludedDateById({ id: editedDate.id, data: formContent }));
    } else {
      dispatch(
        postExcludedDate({
          ...formContent,
          id: uuidv4(),
        })
      );
    }
  };

  return (
    <DialogWrapperWithClose
      open={true}
      onClose={onDialogClose}
      fullWidth
      maxWidth="sm"
      title={editedDate === null ? t("excludedDate-details-title-add") : t("excludedDate-details-title-update")}
    >
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <DateInput
              date={startDate.value}
              title={t("excludedDate-startDate-title") as string}
              dateValidation={startDate.validation}
              onDateTimeChange={startDateChangeHandler}
            />
          </Grid>
          <Grid item xs={6}>
            <DateInput
              date={endDate.value}
              title={t("excludedDate-endDate-title") as string}
              dateValidation={endDate.validation}
              minDate={startDate.value}
              onDateTimeChange={endDateChangeHandler}
            />
          </Grid>
          <Grid item xs={12}>
            <RoomSelect
              roomIds={roomIds}
              availableRooms={availableRooms.filter((r) => !r.isArchived)}
              onRoomIdsChange={roomIdsChangeHandler}
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              label={t("excludedDate-reason-title")}
              value={reason}
              onChange={reasonChangeHandler}
              validationResult={reasonValidationResult}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={repeatAnnually} onChange={repeatAnnuallyChangeHandler} />}
              label={getRepeatAnnuallyString(startDate.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ReadOnlyProtectedButton
          variant="contained"
          color="primary"
          size="small"
          startIcon={<SaveIcon />}
          onClick={applyClickHandler}
          disabled={!reasonValidationResult.isValid || !startDate.validation.isValid || !endDate.validation.isValid}
        >
          {t("button-save")}
        </ReadOnlyProtectedButton>
      </DialogActions>
    </DialogWrapperWithClose>
  );
};

export default ExcludedDateDetails;
